/**
 * Typed API client for Workers (apps/workers).
 *
 * Source : Sprint 2.1 P5 L9 — single entry point for all frontend → Workers calls.
 * - credentials: 'include' (cookies cross-origin for Better-Auth session)
 * - In prod, API is same-origin at /api/* (route Workers via wrangler.toml)
 * - In dev, VITE_API_URL points to local Workers (default http://localhost:8787)
 * - Throws ApiError on non-2xx with { code, message, status }
 */
export const API_BASE_URL = import.meta.env.PROD
    ? (typeof window !== 'undefined' ? window.location.origin : '')
    : (import.meta.env.VITE_API_URL ?? 'http://localhost:8787');
export class ApiError extends Error {
    status;
    code;
    constructor(message, status, code) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.code = code;
    }
}
async function parseError(res) {
    let code = 'unknown';
    let message = `HTTP ${res.status}`;
    try {
        const body = (await res.json());
        if (typeof body.code === 'string')
            code = body.code;
        if (typeof body.message === 'string')
            message = body.message;
    }
    catch {
        /* ignore parse failure, use defaults */
    }
    return new ApiError(message, res.status, code);
}
export async function apiGet(path, opts = {}) {
    const res = await fetch(`${API_BASE_URL}${path}`, {
        method: 'GET',
        credentials: 'include',
        headers: { Accept: 'application/json', ...opts.headers },
        signal: opts.signal,
    });
    if (!res.ok)
        throw await parseError(res);
    return (await res.json());
}
export async function apiPost(path, body, opts = {}) {
    const res = await fetch(`${API_BASE_URL}${path}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...opts.headers },
        body: JSON.stringify(body),
        signal: opts.signal,
    });
    if (!res.ok)
        throw await parseError(res);
    return (await res.json());
}
export async function apiPut(path, body, opts = {}) {
    const res = await fetch(`${API_BASE_URL}${path}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...opts.headers },
        body: JSON.stringify(body),
        signal: opts.signal,
    });
    if (!res.ok)
        throw await parseError(res);
    return (await res.json());
}
/** RGPD art. 20 — download user's full data as JSON file. */
export async function downloadUserDataFromServer() {
    const res = await fetch(`${API_BASE_URL}/api/data/export`, {
        method: 'GET',
        credentials: 'include',
    });
    if (!res.ok)
        throw await parseError(res);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `labrain-data-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 0);
}
/** RGPD art. 17 — hard-delete account + cascade. */
export async function deleteAccountOnServer() {
    return apiPost('/api/account/delete', {});
}
//# sourceMappingURL=api-client.js.map